import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StatusType, TaskType } from "App.types";
import { Button } from "antd";
import API from "Api";
import { SoundOutlined } from "@ant-design/icons";
import styles from "./SentenceTask.module.scss";
import cx from "classnames";
import { SentenceTaskProps } from "./SentenceTask.type";
import { STATIC_URL, successMessage } from "App.constants";
import { shuffle, slice, uniq } from "lodash";
import { isMobile } from "react-device-detect";
import TaskPanel from "Components/TaskPanel";
import { NotifyContext } from "App";

const PictureSelectTask: FC<SentenceTaskProps> = ({
  sentences = [],
  sentence: { id, media, alternatives, storyId, optionStory, linkedStory },
  lesson,
  onTaskComplete,
  onNext,
  audio,
  setDictOpened,
  showGrammar,
  setTry,
  showSuccessMessage,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [selectedAnswer, setSelectedAnswer] = useState<string>();

  const optionPictures = useMemo(
    () =>
      [...(optionStory?.sentences || []), ...(linkedStory?.sentences || [])]
        .map((s) => s.media)
        .filter((s = "") => [".svg", ".jpeg", ".jpg", ".png"].some((ext = "") => s?.endsWith(ext))) ?? [],
    [optionStory?.sentences, linkedStory?.sentences],
  );

  const options = useMemo(
    () =>
      alternatives.filter((alt) => alt.media).length
        ? alternatives.filter((alt) => alt.media).map((alt) => alt.media)
        : optionPictures.length
          ? optionPictures
          : sentences.map((s) => s.media).filter((s = "") => [".svg", ".jpeg", ".jpg", ".png"].some((ext) => s?.endsWith(ext))),
    [alternatives, optionPictures, sentences],
  );

  const rightPicture = useMemo(
    () => media || linkedStory?.sentences.find((s) => s.translate?.includes("+"))?.media,
    [linkedStory?.sentences, media],
  );

  const pictures = useMemo(
    () => shuffle(uniq(slice([rightPicture, ...options], 0, (alternatives.length || -1) + 1 || 6))),
    [rightPicture, options, alternatives.length],
  );

  const notifyApi = useContext(NotifyContext);

  // initial
  useEffect(() => {
    setStatus(StatusType.Editing);
  }, []);

  const onComplete = useCallback(
    (showMessage = true) => {
      setStatus(StatusType.Completed);

      if (showMessage && showSuccessMessage) {
        notifyApi.success(successMessage);
      }

      onTaskComplete();
    },
    [onTaskComplete, notifyApi, showSuccessMessage],
  );

  const onWordGroupClick = (url?: string) => () => {
    if (url === rightPicture) {
      audio?.play();
    }
    // } else {
    //   const audio = user?.isEnglish
    //     ? new Audio(getAudioUrl(text, "uk", sentence.isCard ? undefined : "true"))
    //     : new Audio(getNonEnglishAudioUrl(text, GERMAN_VOICES[0], lesson.story.id, lesson.speechRate || "slow"));
    //   audio.play();
    // }

    if (status === StatusType.Completed) {
      return;
    }

    setSelectedAnswer(url);

    if (rightPicture === url) {
      onComplete(false);
      return StatusType.Completed;
    }

    setTry();

    setStatus(StatusType.Error);

    API.event.save({ sentence: { id }, type: "mistake", task: TaskType.PictureSelect, lesson: { id: lesson.id } });
    return StatusType.Error;
  };

  const onHintAnswer = () => {
    API.event.save({ type: "hint", task: TaskType.PictureSelect, lesson: { id: lesson.id }, sentence: { id } });

    setTry(true);

    onWordGroupClick(rightPicture)();
  };

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.pictures, { [styles.pictures_mobile]: isMobile })}>
        {pictures.map((url) => (
          <img
            className={cx(styles.picture, {
              [styles.btn_red]: selectedAnswer !== rightPicture && url === selectedAnswer,
              [styles.btn_green]: status === StatusType.Completed && url === selectedAnswer,
            })}
            onClick={onWordGroupClick(url)}
            key={url}
            alt={url}
            src={url?.startsWith("http") ? url : `${STATIC_URL}/media/${url}`}
          />

          // <Card
          //   className={cx(styles.picture, {
          //     [styles.btn_red]: selectedAnswer !== rightPicture && url === selectedAnswer,
          //     [styles.btn_green]: status === StatusType.Completed && url === selectedAnswer,
          //   })}
          //   styles={{
          //     body: {
          //       padding: 0,
          //     },
          //   }}
          //   key={url}
          //   onClick={onWordGroupClick(url)}
          //   hoverable
          //   cover={<img className={styles.img} alt={url} src={url?.startsWith("http") ? url : `${STATIC_URL}/media/${url}`} />}
          // />
        ))}
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.PictureSelect}
        sentId={id}
        storyId={storyId}
        showGrammar={showGrammar}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
        setDictOpened={setDictOpened}
        audio={audio}
        onHint={onHintAnswer}
      >
        <Button type={"primary"} icon={<SoundOutlined />} shape={"round"} onClick={() => audio?.play?.()}></Button>
      </TaskPanel>
    </div>
  );
};

export default PictureSelectTask;
